import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ProjectSettings, { ProjectSetupData } from '../components/ProjectSettings';
import { createProject } from '../services/projects';
import { getProfile, Profile as ProfileType } from '../services/profile';
import AppShell from '../components/AppShell';
import InlineError from '../components/InlineError';
import { useIsMobile } from '../hooks/useIsMobile';
import { actionFailureMessage } from '../utils/actionFailure';

export default function ProjectSetup() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const [pendingProject, setPendingProject] = useState<ProjectSetupData | null>(null);
  const [existingProjectNames, setExistingProjectNames] = useState<string[]>([]);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem('pendingProject');
    if (saved) {
      setPendingProject(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getProfile()
        .then((p) => {
          setProfile(p);
          setExistingProjectNames(p.projects.map((x) => x.name));
        })
        .catch(() => {});
    }
  }, [isAuthenticated]);

  const handleProjectSetup = async (data: ProjectSetupData) => {
    if (!isAuthenticated) {
      localStorage.setItem('pendingProject', JSON.stringify(data));
      navigate('/login');
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setCreateError(null);
    try {
      const projectType = data.type === '2D' ? 'TwoD' : 'ThreeD';
      const project = await createProject({
        projectName: data.name,
        prompt: data.description,
        projectType: projectType as 'TwoD' | 'ThreeD',
        displayMode: data.displayMode,
        providedAssets: data.providedAssets,
      });

      localStorage.setItem(
        `project_${project.id}_metadata`,
        JSON.stringify({ type: data.type, displayMode: data.displayMode }),
      );
      localStorage.removeItem('pendingProject');

      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      const errorCode =
        axios.isAxiosError(error) && error.response?.status === 409
          ? (error.response.data as { error?: string } | undefined)?.error
          : null;
      setCreateError(
        errorCode === 'project_name_taken'
          ? t('projectSetup.nameTaken', 'A project with this name already exists. Choose a different name.')
          : actionFailureMessage(error, t('projectSetup.createFailed', 'Failed to create project. Please try again.')),
      );
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell
      active="projects"
      breadcrumbs={[
        { label: t('sidebar.projects', 'Projects'), to: '/profile' },
        { label: t('projectSetup.newProject', 'New project') },
      ]}
      credits={profile?.credits.remainingCredits}
      creditsMax={profile?.credits.totalCredits}
      projectsUsed={profile?.credits.currentProjectCount}
      projectsMax={profile?.credits.maxProjects}
      rightSlot={<span className="mono muted" style={{ fontSize: 12 }}>{t('projectSetup.stepOf', 'Step 1 of 1')}</span>}
    >
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '24px 16px 40px' : '40px 56px 56px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 32 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 12 }}>
              {t('projectSetup.create', 'Create')}
            </div>
            <h1 className="display tight" style={{ fontSize: isMobile ? 26 : 36, margin: '0 0 8px' }}>
              {t('projectSetup.heading', 'What are we building?')}
            </h1>
            <p className="muted-2" style={{ fontSize: 14, margin: 0, maxWidth: 520, lineHeight: 1.55 }}>
              {t(
                'projectSetup.subheading',
                'One sentence is enough to get started. You can keep iterating once the first build lands — this is just the seed.',
              )}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <ProjectSettings
              onSubmit={handleProjectSetup}
              initialData={pendingProject || undefined}
              existingProjectNames={existingProjectNames}
              isSubmitting={isSubmitting}
              canUploadAssets={isAuthenticated}
            />
            {createError && <InlineError>{createError}</InlineError>}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
